import {QuestionResponseNested} from '@letrustech/letrus-api-interfaces/dist/interfaces/letrus_api';
import {useNavigate, useParams} from 'react-router-dom';
import {useQueryParam} from 'utils/hooks';
import useLearningPathNavigation from 'features/useLearningPathNavigation';
import {useEffect, useRef, useState} from 'react';
import usePracticeTest from 'features/usePracticeTest';
import {Steps} from 'components/ui';
import Statement from 'containers/PracticeTest/components/Statement';
import {getUserData} from 'utils/functions/getUserData/getUserData';
import {
  fetchQuestionAttemptRequest,
  fetchQuestionnaireRequest,
  finishQuestionnaireStepRequest,
} from 'APIs/letrusAPI/questionnaire';
import {MissingQuestion} from 'APIs/letrusAPI/questionnaire/types';
import {fetchServerStatusRequest} from 'APIs/letrusAPI/serverStatus';
import {isFocusModeFeedbackReleased, useFocusMode} from 'contexts/FocusMode';
import QuestionnaireHeader from 'containers/NewQuestionnaire/components/QuestionnaireHeader';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import CircularTimer from './components/CircularTimer';
import styles from './PracticeTest.module.scss';
import RadioQuestion from './components/RadioQuestion';
import CheckboxQuestion from './components/CheckboxQuestion';
import TextQuestion from './components/TextQuestion';
import RatingQuestion from './components/RatingQuestion';
import PracticeTestFinishModal from './components/PracticeTestFinishModal';
import PracticeTestMissingQuestionsModal from './components/PracticeTestMissingQuestionsModal';

export interface QuestionSubmitParams {
  questionId: number;
  answer?: string;
  responses?: Partial<QuestionResponseNested>[];
}

function PracticeTest(): JSX.Element {
  const [totalTime, setTotalTime] = useState(0);
  const [remainingTime, setRemainingTime] = useState(0);
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [statusTimer, setStatusTimer] =
    useState<'waiting' | 'running' | 'finished'>('waiting');
  const [missingQuestions, setMissingQuestions] = useState<MissingQuestion[]>(
    [],
  );
  const [showMissingQuestionsModal, setShowMissingQuestionsModal] =
    useState(false);

  const {user} = getUserData();
  const navigate = useNavigate();
  const queryParams = useQueryParam();
  const {questionnaire_id} = useParams();
  const startTimeRef = useRef<Date | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const learningPathUserInstanceId = queryParams.get('trilha') ?? '';

  useEffect(
    () => () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    },
    [],
  );

  const {
    isActive,
    isExpired,
    isOffline,
    remainingSeconds,
    effectiveTimeSeconds,
    isInitializingTimer,
    setExpireModalMessages,
  } = useFocusMode();

  // Focus Mode expiration: no callback needed here.
  // FocusModeProvider handles the modal + dashboard redirect automatically.
  // The backend finalizes the trail based on question_attempts already received.
  // Set questionnaire-specific expire modal messages.
  useEffect(() => {
    const restore = setExpireModalMessages({
      doneMessage: 'Suas respostas foram registradas.',
    });
    return restore;
  }, [setExpireModalMessages]);

  const fetchServerStatus = fetchServerStatusRequest();
  const questionnaire = fetchQuestionnaireRequest(questionnaire_id);
  const finishQuestionnaireStep = finishQuestionnaireStepRequest(
    learningPathUserInstanceId,
  );
  const {learningPath, navigateToLearningPath} = useLearningPathNavigation(
    learningPathUserInstanceId,
  );

  const isLearningPathFinished = learningPath?.status === 'finished';
  const isFocusModeQuestionnaire = !!learningPath?.uses_focus_mode;
  const learningPathStep = learningPath?.steps.find(
    (step) => step.activity_id === questionnaire?.data?.id,
  );
  const learningPathStepId = learningPathStep?.id;

  const questionAttempts = fetchQuestionAttemptRequest({
    step: learningPathStepId,
    learning_path_user_instance: learningPathUserInstanceId,
  });

  const isPracticeTestFinished = !!questionAttempts.data?.results.some(
    (attempt) => attempt.is_finished,
  );

  const answersVisible = isFocusModeQuestionnaire
    ? isFocusModeFeedbackReleased(learningPath)
    : isLearningPathFinished;

  const isStepAlreadyPassed =
    (learningPath?.step_number ?? 0) > (learningPathStep?.order ?? 0);
  const isReadOnly =
    isExpired ||
    isOffline ||
    isLearningPathFinished ||
    learningPath?.status === 'expired' ||
    isPracticeTestFinished ||
    isStepAlreadyPassed ||
    questionAttempts.isLoading;

  const {
    currentQuestion,
    submitQuestionAnswer,
    returnToPreviousQuestion,
    goToNextQuestion,
    goToQuestionByOrder,
  } = usePracticeTest({
    questionnaire: questionnaire?.data,
    questionAttempt: questionAttempts?.data?.results,
    learningPathUserInstanceId,
    learningPathStepId,
  });

  useEffect(() => {
    if (questionnaire.data && learningPath) {
      if (isLearningPathFinished) {
        setStatusTimer('finished');
      } else if (isFocusModeQuestionnaire && isActive) {
        // Focus Mode: timer starts immediately when page is accessed
        if (!isInitializingTimer) {
          setStatusTimer('running');
          setRemainingTime(remainingSeconds);
        }
      } else if (learningPath.started) {
        // Regular mode: timer starts after first question is answered
        setStatusTimer('running');
        // if the start time is not set by learning path started, will be set by the first heartbeat
        startTimeRef.current = new Date(learningPath.started);
        setTotalTime(
          getRemainingTime(
            learningPath.learning_path_timer,
            learningPath.end_datetime,
            new Date(),
            undefined,
          ),
        );
      } else {
        // if the start time is not set by learning path started, will be set by the first heartbeat
        startTimeRef.current = null;
        setTotalTime(
          getRemainingTime(
            learningPath.learning_path_timer,
            learningPath.end_datetime,
            new Date(),
            undefined,
          ),
        );
      }
    }
  }, [
    questionnaire.data,
    learningPath,
    isActive,
    effectiveTimeSeconds,
    remainingSeconds,
    isInitializingTimer,
  ]);

  useEffect(() => {
    let heartbeatId: NodeJS.Timeout | null = null;

    // In Focus Mode, sync is handled by FocusModeProvider
    if (statusTimer === 'running' && !isFocusModeQuestionnaire) {
      const syncWithServer = async () => {
        try {
          const data = await fetchServerStatus.trigger(null);
          const nowOnServer = new Date(data?.current_utc_time || '');
          const started = startTimeRef.current || nowOnServer;

          const newRemainingTime = getRemainingTime(
            learningPath?.learning_path_timer,
            learningPath?.end_datetime,
            nowOnServer,
            started,
          );

          if (newRemainingTime <= 0) {
            setStatusTimer('finished');
            return;
          }

          // if the start time is not set by learning path started yet, will be set by the first heartbeat
          startTimeRef.current = startTimeRef.current || nowOnServer;

          setRemainingTime(newRemainingTime);
        } catch (error) {
          // TODO: add error handling
          console.error('heartbeat error:', error);
        }
      };

      syncWithServer();
      heartbeatId = setInterval(syncWithServer, 60000);
    }

    return () => {
      if (heartbeatId) {
        clearInterval(heartbeatId);
      }
    };
  }, [statusTimer, isActive, learningPath, fetchServerStatus]);

  useEffect(() => {
    let timerId: NodeJS.Timeout | null = null;

    // In Focus Mode, use remaining seconds from FocusModeProvider
    if (isActive && statusTimer === 'running') {
      setRemainingTime(remainingSeconds);
      if (remainingSeconds <= 0) {
        setStatusTimer('finished');
      }
    } else if (statusTimer === 'running' && remainingTime > 0) {
      // Regular mode countdown
      timerId = setInterval(() => {
        setRemainingTime((prevTime) => prevTime - 1);
      }, 1000);
    } else if (remainingTime < 0 && statusTimer === 'running') {
      setStatusTimer('finished');
    }

    return () => {
      if (timerId) {
        clearInterval(timerId);
      }
    };
  }, [statusTimer, remainingTime, isActive, remainingSeconds]);

  useEffect(() => {
    // when the timer is finished and the learning path is not finished yet
    // Only for regular mode - Focus Mode is handled by FocusModeProvider
    if (
      statusTimer === 'finished' &&
      !isLearningPathFinished &&
      !isFocusModeQuestionnaire
    ) {
      setShowFinishModal(true);
    }
  }, [statusTimer, isLearningPathFinished, isActive, learningPath]);

  function navigateAfterFinish() {
    navigateToLearningPath.nextStep(Number(questionnaire_id));

    // Regular questionnaire - redirect to result page
    // Focus Mode just advances learning path and returns above
    if (!isFocusModeQuestionnaire) {
      navigate(
        {
          pathname: `/atividades/simulado/${questionnaire_id}/resultado`,
          search: `trilha=${learningPathUserInstanceId}&step=${learningPathStepId}`,
        },
        {
          replace: true,
        },
      );
    }
  }

  // Submitting the last answer (RadioQuestion.handleSubmit and similar) triggers the completion
  // check without waiting for the save to finish on the backend — when the answer hasn't
  // persisted yet at the exact moment of the check, the backend responds "pending question"
  // even though the answer is already marked on screen (found live, 2026-09-22). Fixing the
  // root cause would require making the save's redux-saga dispatch awaitable, a bigger change;
  // this single retry covers the real case without touching the save flow.
  function endPracticeTest(isRetry = false) {
    if (isOffline) return;

    // Skip validation only for regular mode when the timer expired — the student
    // can no longer answer missing questions. In Focus Mode we always validate:
    // FocusModeProvider already handles the expiry modal/redirect independently.
    const isRegularModeTimerExpired =
      !isFocusModeQuestionnaire && statusTimer === 'finished';

    if (isRegularModeTimerExpired) {
      navigateAfterFinish();
      return;
    }

    finishQuestionnaireStep?.trigger(undefined, {
      onSuccess: () => {
        navigateAfterFinish();
      },
      onError: (error) => {
        const responseData = error?.response?.data;
        const isMissingQuestions =
          error?.response?.status === 400 &&
          responseData?.missing_questions?.length;

        if (isMissingQuestions && !isRetry) {
          retryTimeoutRef.current = setTimeout(
            () => endPracticeTest(true),
            800,
          );
        } else if (isMissingQuestions) {
          setMissingQuestions(responseData.missing_questions);
          setShowMissingQuestionsModal(true);
        } else {
          navigateAfterFinish();
        }
      },
    });
  }

  function handleGoBackToMissingQuestion() {
    setShowMissingQuestionsModal(false);

    if (missingQuestions.length > 0) {
      goToQuestionByOrder(missingQuestions[0].order);
    }
  }

  // returns the minimum remaining time in seconds between the learning path timer and the end datetime (absolute deadline)
  function getRemainingTime(
    learningPathTimer: number | undefined,
    endDatetime: string | undefined,
    now: Date,
    started?: Date | null,
  ): number {
    const totalDurationSeconds = learningPathTimer || 0;
    let timeUntilEndDatetime = Infinity;
    if (endDatetime) {
      const end = new Date(endDatetime);
      timeUntilEndDatetime = (end.getTime() - now.getTime()) / 1000;
    }

    let timeByTimer = totalDurationSeconds;
    if (started) {
      const elapsed = (now.getTime() - started.getTime()) / 1000;
      timeByTimer = totalDurationSeconds - elapsed;
    }
    return Math.min(timeByTimer, timeUntilEndDatetime);
  }

  async function onQuestionSubmit({
    answer,
    questionId,
    responses,
  }: QuestionSubmitParams) {
    if (isReadOnly) return;

    // In Focus Mode, timer already running - no need to start it
    if (isFocusModeQuestionnaire) {
      submitQuestionAnswer({
        newAnswer: {
          step: Number(learningPathStepId),
          user: user.id,
          source: 'web',
          answer,
          question: questionId,
          responses: responses?.map((item) => ({
            option: item.option,
            feedback: item.feedback,
          })),
          learning_path_user_instance: Number(learningPathUserInstanceId),
        },
      });
      return;
    }

    // First question answered in regular mode
    if (!learningPath?.started && responses?.length === 0) {
      setRemainingTime(
        getRemainingTime(
          learningPath?.learning_path_timer,
          learningPath?.end_datetime,
          new Date(),
          undefined,
        ),
      );
      setStatusTimer('running');
    }

    submitQuestionAnswer({
      newAnswer: {
        step: Number(learningPathStepId),
        user: user.id,
        source: 'web',
        answer,
        question: questionId,
        responses: responses?.map((item) => ({
          option: item.option,
          feedback: item.feedback,
        })),
        learning_path_user_instance: Number(learningPathUserInstanceId),
      },
    });
  }

  return (
    <main className={styles.wrapper}>
      {isFocusModeQuestionnaire ? (
        /* Focus Mode: Use QuestionnaireHeader with FocusModeTimer */
        <QuestionnaireHeader
          name={questionnaire?.data?.name}
          learningPath={learningPath}
          onGoBack={() => navigate(-1)}
          isActive={isActive}
          remainingSeconds={remainingSeconds}
          effectiveTimeSeconds={effectiveTimeSeconds}
          isExpired={remainingSeconds <= 0 && !isInitializingTimer}
        />
      ) : (
        /* Regular Mode: Use original header */
        <header className={styles.header}>
          <div className={styles.questionnaireInfo}>
            <button
              type="button"
              aria-label="Voltar"
              onClick={() => navigate(-1)}
            >
              <FontAwesomeIcon icon="angle-left" size="xl" />
            </button>

            <div>
              {learningPath && (
                <h2 className={styles.title}>{learningPath?.name}</h2>
              )}
              <p className={styles.subtitle}>{questionnaire?.data?.name}</p>
            </div>
          </div>

          <div className={styles.timer}>
            <CircularTimer
              totalTime={totalTime}
              remainingTime={remainingTime}
              status={statusTimer}
            />
          </div>
        </header>
      )}

      <section className={styles.questions}>
        <Steps
          questionnaire
          step={currentQuestion?.order}
          amount={questionnaire?.data?.steps?.length}
        />

        {currentQuestion.question_type === 'radio' && (
          <RadioQuestion
            question={currentQuestion}
            onGoBack={returnToPreviousQuestion}
            onQuestionSubmit={onQuestionSubmit}
            goToNextQuestion={() => goToNextQuestion(endPracticeTest)}
            isQuestionnaireFinished={isReadOnly}
            answersVisible={answersVisible}
            isLoading={questionnaire?.isLoading}
          />
        )}
        {currentQuestion.question_type === 'statement' && (
          <Statement
            question={currentQuestion}
            onGoBack={returnToPreviousQuestion}
            onQuestionSubmit={onQuestionSubmit}
            goToNextQuestion={() => goToNextQuestion(endPracticeTest)}
            isLoading={questionnaire?.isLoading}
            disableTextSelection
          />
        )}
        {currentQuestion.question_type === 'checkbox' && (
          <CheckboxQuestion
            question={currentQuestion}
            onGoBack={returnToPreviousQuestion}
            onQuestionSubmit={onQuestionSubmit}
            goToNextQuestion={() => goToNextQuestion(endPracticeTest)}
            isQuestionnaireFinished={isReadOnly}
            isLoading={questionnaire?.isLoading}
          />
        )}
        {currentQuestion.question_type === 'text' && (
          <TextQuestion
            question={currentQuestion}
            onGoBack={returnToPreviousQuestion}
            onQuestionSubmit={onQuestionSubmit}
            goToNextQuestion={() => goToNextQuestion(endPracticeTest)}
            isQuestionnaireFinished={isReadOnly}
            answersVisible={answersVisible}
            isLoading={questionnaire?.isLoading}
          />
        )}
        {currentQuestion.question_type === 'rating' && (
          <RatingQuestion
            question={currentQuestion}
            onGoBack={returnToPreviousQuestion}
            onQuestionSubmit={onQuestionSubmit}
            goToNextQuestion={() => goToNextQuestion(endPracticeTest)}
            isQuestionnaireFinished={isReadOnly}
            isLoading={questionnaire?.isLoading}
          />
        )}
      </section>

      {showFinishModal && (
        <PracticeTestFinishModal
          onClick={navigateAfterFinish}
          onClose={navigateAfterFinish}
        />
      )}

      {showMissingQuestionsModal && (
        <PracticeTestMissingQuestionsModal
          missingQuestions={missingQuestions}
          onGoBack={handleGoBackToMissingQuestion}
          onClose={() => setShowMissingQuestionsModal(false)}
        />
      )}
    </main>
  );
}

export default PracticeTest;
